import { Channel } from "redux-saga";
import { actionChannel, call, delay, flush, fork, put, select, take, takeEvery } from "redux-saga/effects";

import { bridge } from "@multiweb/reactCore/bridge";
import { SagaWithReturnType, SagaYieldReturn } from "@multiweb/types/react";
import { createLoadLocalizationKeysAction } from "../i18n";
import { createLoadEnumsAction, createSetEnumsAction } from "./actions";
import { BATCHING_DELAY, FETCHING_KEY_ENUMS_LOCALIZATION } from "./constants";
import { logger } from "./logger";
import { getUninitializedEnums } from "./selectors";
import { EnumTypeWithKey, ILoadEnumsAction } from "./types";
import { convertToReduxEnumType, createLoadEnumActionPattern, extractLocKeys, mergeLoadEnumsActions } from "./utils";

const { getEnumData, errorHandler } = bridge;

export const saga: SagaWithReturnType = function* enumsSaga() {
    yield fork(batchingLoadEnumsSaga);
    yield takeEvery(createLoadEnumActionPattern(false), loadEnumsSaga);
};

export const batchingLoadEnumsSaga: SagaWithReturnType = function* batchingLoadEnumSaga() {
    const loadEnumsActionChannel: Channel<ILoadEnumsAction> = yield actionChannel(createLoadEnumActionPattern(true));
    logger.debug("Loading enum saga for action batching is initialized.");
    while (true) {
        const firstAction: ILoadEnumsAction = yield take(loadEnumsActionChannel);
        yield delay(BATCHING_DELAY);
        const bufferedActions: ReadonlyArray<ILoadEnumsAction> = yield flush(loadEnumsActionChannel);
        const allActions = [firstAction, ...bufferedActions];
        const mergedActionsData: ReturnType<typeof mergeLoadEnumsActions> = yield call(mergeLoadEnumsActions, allActions);
        logger.debug(() => `Batched ${allActions.length} actions after ${BATCHING_DELAY}ms. Aggregated actions data: ${JSON.stringify(mergedActionsData)}`);
        yield call(loadEnumsSaga, createLoadEnumsAction(mergedActionsData));
    }
};

/**
 * Obsluhuje požadavky na načtení dat týkajících se enumů. Načtená data doplní k ostatním položkám ve stavu. Lokalizační klíče uloží do i18n modulu.
 */
export const loadEnumsSaga: SagaWithReturnType<[ILoadEnumsAction]> = function* loadEnumSaga(action) {
    const enumsKeys = action.payload.enumsKeys;
    const uninitializedEnums: SagaYieldReturn<typeof getUninitializedEnums> = yield select(getUninitializedEnums, enumsKeys);
    const enumTypesWithKeys: EnumTypeWithKey[] = [];
    try {
        for (const en of uninitializedEnums) {
            const enumType: SagaYieldReturn<typeof getEnumData> = yield call(getEnumData, en);
            const reduxEnumType: SagaYieldReturn<typeof convertToReduxEnumType> = yield call(convertToReduxEnumType, enumType);
            enumTypesWithKeys.push({
                enumKey: en,
                enumType: reduxEnumType,
            });
        }
        // uloží enumy do reduxu
        yield put(createSetEnumsAction(enumTypesWithKeys));
        // uloží lokalizační klíče do reduxu
        yield put(createLoadLocalizationKeysAction(extractLocKeys(enumTypesWithKeys), FETCHING_KEY_ENUMS_LOCALIZATION, true));
    } catch (error) {
        yield call(errorHandler, error);
    }
};
